// Run this in browser console OR import and call restoreHeithInventory()
// This restores Heith's inventory at Choctaw Beach, FL

import { logger } from '@/utils/logger'

const log = logger.module('RestoreHeithInventory')

export interface PartnerMaterial {
  id: string
  type: string
  material: string
  name: string
  thickness: string
  size: string
  quantity: number
  cost: number
  location: string
  notes: string
  addedAt: string
}

export interface PartnerInventory {
  materials: PartnerMaterial[]
  lastUpdated: string
  lastVerified: string
  verificationPending: boolean
}

export interface PartnerInventories {
  [partnerId: string]: PartnerInventory
}

export interface ScrapPiece {
  id: string
  material: string
  species: string
  dimensions: {
    length: number
    width: number
    thickness: number
  }
  partnerId: string
  partnerName: string
  location: string
  usableFor: string[]
  estimatedValue: number
  reusePriority: string
  dateAdded: string
  used: boolean
  usedFor: null | string
  allocatedTo: null | string
}

export interface ScrapInventory {
  plywood: ScrapPiece[]
  lumber: ScrapPiece[]
}

export interface Order {
  claimedByPartner?: string
  claimedByPartnerName?: string
  claimedByPartnerLocation?: string
  fabricationStatus?: string
  [key: string]: any
}

export interface RestoreResult {
  partnerInventories: PartnerInventories
  scrapInventory: ScrapInventory
}

export function restoreHeithInventory(): RestoreResult {
  // DEPRECATED: Inventory is now in Supabase - test data should be managed via Supabase admin
  // This function is kept for reference but no longer writes to localStorage
  console.warn('[DEPRECATED] restoreHeithInventory - use Supabase admin for test data')
  
  // Partner Inventory - Sheet Goods (not saved to localStorage anymore)
  const partnerInventories: PartnerInventories = {}

  // Use material names that match BOM requirements
  // The BOM uses names like "11/16" Birch", "1/2" Birch", "1/4" Birch"
  partnerInventories['choctaw-beach'] = {
    materials: [
      {
        id: `MAT-${Date.now()}-birch1116`,
        type: 'sheet',
        material: '11/16" Birch',  // Match BOM naming convention
        name: '11/16" Birch',
        thickness: '11/16"',
        size: "4' x 8'",
        quantity: 11,
        cost: 85,  // Approx cost per sheet
        location: 'Choctaw Beach Shop',
        notes: 'Birch plywood 4x8 sheets',
        addedAt: new Date().toISOString()
      },
      {
        id: `MAT-${Date.now()}-birch14`,
        type: 'sheet',
        material: '1/4" Birch',  // Match BOM naming convention
        name: '1/4" Birch',
        thickness: '1/4"',
        size: "4' x 8'",
        quantity: 2,
        cost: 45,  // Approx cost per sheet
        location: 'Choctaw Beach Shop',
        notes: 'Birch plywood 4x8 sheets',
        addedAt: new Date().toISOString()
      }
    ],
    lastUpdated: new Date().toISOString(),
    lastVerified: new Date().toISOString(),
    verificationPending: false
  }

  // DEPRECATED: No longer saving to localStorage - Supabase is source of truth
  // localStorage.setItem('blissPartnerInventories', JSON.stringify(partnerInventories))

  // Scrap Bank - structure only (not saved to localStorage)
  const scrapInventory: ScrapInventory = { plywood: [], lumber: [] }

  // Example scrap data structure for reference only
  for (let i = 0; i < 8; i++) {
    scrapInventory.plywood.push({
      id: `SCRAP-${Date.now()}-${i}-${Math.random().toString(36).substr(2, 5)}`,
      material: '11/16" Birch',
      species: 'Birch',
      dimensions: {
        length: 48,
        width: 19,
        thickness: 0.6875
      },
      partnerId: 'choctaw-beach',
      partnerName: 'Heith R.',
      location: 'partner',
      usableFor: ['Platform Bottom', 'Bed Box Side'],
      estimatedValue: 12,
      reusePriority: 'MEDIUM',
      dateAdded: new Date().toISOString(),
      used: false,
      usedFor: null,
      allocatedTo: null
    })
  }

  // DEPRECATED: No longer saving to localStorage - Supabase is source of truth
  // localStorage.setItem('blissScrapInventory', JSON.stringify(scrapInventory))

  // DEPRECATED: Orders are now in Supabase - test data should be managed via Supabase admin
  log.info('[DEPRECATED] restoreHeithInventory - order assignment skipped, use Supabase admin')

  log.info('Heith inventory restored!', {
    partnerId: 'choctaw-beach',
    birch1116Sheets: 11,
    birch14Sheets: 2,
    scrapPieces: 8
  })

  return { partnerInventories, scrapInventory }
}

// Auto-run when imported in dev
if (typeof window !== 'undefined' && window.location.hostname === 'localhost') {
  // Make available globally for console access
  (window as any).restoreHeithInventory = restoreHeithInventory
}
